/**
 * PremiumToast.jsx
 * Premium Toast Notification System
 *
 * Design System Premium Fleet v2.0
 * Inspired by Sonner, React Hot Toast
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  alpha,
  keyframes,
  styled,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../theme/usePremiumTokens';
import { useTranslation } from '../LocalizationProvider';

// ============================================================================
// ANIMATIONS
// ============================================================================

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideOutRight = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100%);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideOutLeft = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-100%);
  }
`;

const slideInTop = keyframes`
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideOutTop = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-100%);
  }
`;

const slideInBottom = keyframes`
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideOutBottom = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(100%);
  }
`;

const progressShrink = keyframes`
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
`;

// ============================================================================
// TOAST CONTEXT
// ============================================================================

const ToastContext = createContext(null);

// ============================================================================
// TOAST TYPES CONFIG
// ============================================================================

const getToastConfig = (type, tokens) => {
  const configs = {
    success: {
      icon: <CheckCircleIcon />,
      color: tokens?.semantic?.success || '#22c55e',
      bgColor: alpha(tokens?.semantic?.success || '#22c55e', 0.1),
      borderColor: alpha(tokens?.semantic?.success || '#22c55e', 0.3),
    },
    error: {
      icon: <ErrorIcon />,
      color: tokens?.semantic?.error || '#ef4444',
      bgColor: alpha(tokens?.semantic?.error || '#ef4444', 0.1),
      borderColor: alpha(tokens?.semantic?.error || '#ef4444', 0.3),
    },
    warning: {
      icon: <WarningIcon />,
      color: tokens?.semantic?.warning || '#f59e0b',
      bgColor: alpha(tokens?.semantic?.warning || '#f59e0b', 0.1),
      borderColor: alpha(tokens?.semantic?.warning || '#f59e0b', 0.3),
    },
    info: {
      icon: <InfoIcon />,
      color: tokens?.semantic?.info || '#3b82f6',
      bgColor: alpha(tokens?.semantic?.info || '#3b82f6', 0.1),
      borderColor: alpha(tokens?.semantic?.info || '#3b82f6', 0.3),
    },
    loading: {
      icon: <CircularProgress size={20} thickness={4} />,
      color: tokens?.text?.secondary || '#9ca3af',
      bgColor: tokens?.surface?.elevated || '#1f2937',
      borderColor: tokens?.border?.subtle || '#374151',
    },
  };

  return configs[type] || configs.info;
};

// ============================================================================
// POSITION CONFIG
// ============================================================================

const getPositionStyles = (position) => {
  const positions = {
    'top-left': {
      top: 16,
      left: 16,
      alignItems: 'flex-start',
      enterAnimation: slideInLeft,
      exitAnimation: slideOutLeft,
    },
    'top-center': {
      top: 16,
      left: '50%',
      transform: 'translateX(-50%)',
      alignItems: 'center',
      enterAnimation: slideInTop,
      exitAnimation: slideOutTop,
    },
    'top-right': {
      top: 16,
      right: 16,
      alignItems: 'flex-end',
      enterAnimation: slideInRight,
      exitAnimation: slideOutRight,
    },
    'bottom-left': {
      bottom: 16,
      left: 16,
      alignItems: 'flex-start',
      enterAnimation: slideInLeft,
      exitAnimation: slideOutLeft,
    },
    'bottom-center': {
      bottom: 16,
      left: '50%',
      transform: 'translateX(-50%)',
      alignItems: 'center',
      enterAnimation: slideInBottom,
      exitAnimation: slideOutBottom,
    },
    'bottom-right': {
      bottom: 16,
      right: 16,
      alignItems: 'flex-end',
      enterAnimation: slideInRight,
      exitAnimation: slideOutRight,
    },
  };

  return positions[position] || positions['bottom-right'];
};

// ============================================================================
// TOAST COMPONENT
// ============================================================================

const ToastWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
}));

export const Toast = ({
  id,
  type = 'info',
  message,
  description,
  action,
  duration = 4000,
  dismissible = true,
  onDismiss,
  position = 'bottom-right',
  isExiting = false,
  pauseOnHover = true,
}) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(100);
  const startTimeRef = useRef(Date.now());
  const remainingTimeRef = useRef(duration);
  const timerRef = useRef(null);

  const config = getToastConfig(type, tokens);
  const positionConfig = getPositionStyles(position);

  // Unified timer effect - handles setup, progress, and dismissal
  useEffect(() => {
    // Skip timer for loading toasts or infinite duration
    if (type === 'loading' || duration === Infinity || duration <= 0) {
      setProgress(100);
      return;
    }

    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Reset progress and timing
    setProgress(100);
    remainingTimeRef.current = duration;
    startTimeRef.current = Date.now();
    setIsPaused(false);

    // Set up timer
    const tick = () => {
      if (isPaused) return;

      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, remainingTimeRef.current - elapsed);
      const newProgress = (remaining / duration) * 100;
      setProgress(newProgress);

      if (remaining <= 0) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        onDismiss?.(id);
      }
    };

    timerRef.current = setInterval(tick, 50);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [type, duration, id, onDismiss]);

  // Handle pause/resume separately
  useEffect(() => {
    if (type === 'loading' || duration === Infinity) return;

    if (isPaused) {
      // Pausing - save remaining time
      const elapsed = Date.now() - startTimeRef.current;
      remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
    } else {
      // Resuming - reset start time
      startTimeRef.current = Date.now();
    }
  }, [isPaused, type, duration]);

  const handleMouseEnter = () => {
    if (pauseOnHover && type !== 'loading') {
      const elapsed = Date.now() - startTimeRef.current;
      remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const handleMouseLeave = () => {
    if (pauseOnHover && type !== 'loading') {
      setIsPaused(false);
    }
  };

  const handleDismiss = () => {
    onDismiss?.(id);
  };

  const handleAction = () => {
    action?.onClick?.();
    if (action?.dismissOnClick !== false) {
      onDismiss?.(id);
    }
  };

  return (
    <ToastWrapper
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 2,
        pr: dismissible ? 5 : 2,
        borderRadius: tokens?.radius?.lg || 12,
        bgcolor: tokens?.surface?.elevated || '#1f2937',
        backdropFilter: 'blur(12px)',
        border: `1px solid ${config.borderColor}`,
        boxShadow: tokens?.shadows?.lg || '0 10px 15px -3px rgba(0,0,0,0.3)',
        minWidth: 300,
        maxWidth: 420,
        animation: `${(isExiting ? positionConfig.exitAnimation : positionConfig.enterAnimation)?.name || 'none'} 200ms ease-out`,
        animationFillMode: 'forwards',
        transition: 'all 200ms ease',
        '&:hover': {
          boxShadow: tokens?.shadows?.xl || '0 20px 25px -5px rgba(0,0,0,0.3)',
        },
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: 24,
          height: 24,
          color: config.color,
          '& svg': {
            fontSize: 22,
          },
        }}
      >
        {config.icon}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: tokens?.text?.primary || '#f9fafb',
            lineHeight: 1.4,
          }}
        >
          {message}
        </Typography>

        {description && (
          <Typography
            sx={{
              mt: 0.5,
              fontSize: 13,
              color: tokens?.text?.secondary || '#9ca3af',
              lineHeight: 1.4,
            }}
          >
            {description}
          </Typography>
        )}

        {action && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={handleAction}
              sx={{
                px: 1.5,
                py: 0.5,
                fontSize: 12,
                fontWeight: 600,
                color: config.color,
                bgcolor: alpha(config.color, 0.1),
                borderRadius: tokens?.radius?.md || 6,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: alpha(config.color, 0.2),
                },
              }}
            >
              {action.label}
            </Button>
            {action.secondaryAction && (
              <Button
                size="small"
                onClick={() => {
                  action.secondaryAction.onClick?.();
                  onDismiss?.(id);
                }}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  fontSize: 12,
                  fontWeight: 500,
                  color: tokens?.text?.secondary || '#9ca3af',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: alpha(tokens?.text?.secondary || '#9ca3af', 0.1),
                  },
                }}
              >
                {action.secondaryAction.label}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Close button */}
      {dismissible && type !== 'loading' && (
        <IconButton
          size="small"
          onClick={handleDismiss}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            p: 0.5,
            color: tokens?.text?.tertiary || '#6b7280',
            '&:hover': {
              color: tokens?.text?.secondary || '#9ca3af',
              bgcolor: alpha(tokens?.text?.secondary || '#9ca3af', 0.1),
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}

      {/* Progress bar */}
      {type !== 'loading' && duration !== Infinity && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            bgcolor: alpha(config.color, 0.2),
            borderRadius: `0 0 ${tokens?.radius?.lg || 12}px ${tokens?.radius?.lg || 12}px`,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${progress}%`,
              bgcolor: config.color,
              transition: isPaused ? 'none' : 'width 50ms linear',
            }}
          />
        </Box>
      )}
    </ToastWrapper>
  );
};

// ============================================================================
// TOAST CONTAINER
// ============================================================================

export const ToastContainer = ({ toasts, position, onDismiss }) => {
  const positionConfig = getPositionStyles(position);
  const isTop = position.startsWith('top');

  // Extract only CSS position properties (exclude animation objects)
  const { enterAnimation, exitAnimation, ...positionStyles } = positionConfig;

  return createPortal(
    <Box
      sx={{
        position: 'fixed',
        zIndex: 9999,
        display: 'flex',
        flexDirection: isTop ? 'column' : 'column-reverse',
        gap: 1.5,
        pointerEvents: 'none',
        ...positionStyles,
        '& > *': {
          pointerEvents: 'auto',
        },
      }}
    >
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          {...toast}
          position={position}
          onDismiss={onDismiss}
        />
      ))}
    </Box>,
    document.body
  );
};

// ============================================================================
// TOAST PROVIDER
// ============================================================================

let toastCounter = 0;

export const ToastProvider = ({
  children,
  position = 'bottom-right',
  maxToasts = 5,
  defaultDuration = 4000,
}) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = toast.id || `toast-${++toastCounter}`;

    setToasts((prev) => {
      // Check if toast with same id exists (for updating)
      const existingIndex = prev.findIndex((t) => t.id === id);

      if (existingIndex !== -1) {
        // Update existing toast - apply default duration if not specified
        // This fixes loading->success transition where loading has Infinity duration
        const newToasts = [...prev];
        const updatedToast = {
          ...newToasts[existingIndex],
          ...toast,
          id,
          isExiting: false,
          // If updating from loading type and no duration specified, use default
          duration: toast.duration ?? (toast.type !== 'loading' ? defaultDuration : Infinity),
        };
        newToasts[existingIndex] = updatedToast;
        return newToasts;
      }

      // Add new toast
      const newToast = {
        ...toast,
        id,
        duration: toast.duration ?? defaultDuration,
        isExiting: false,
      };

      // Limit max toasts
      const updatedToasts = [...prev, newToast];
      if (updatedToasts.length > maxToasts) {
        return updatedToasts.slice(-maxToasts);
      }

      return updatedToasts;
    });

    return id;
  }, [maxToasts, defaultDuration]);

  const dismissToast = useCallback((id) => {
    // Mark as exiting for animation
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, isExiting: true } : t))
    );

    // Remove after animation
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 200);
  }, []);

  const dismissAll = useCallback(() => {
    setToasts((prev) => prev.map((t) => ({ ...t, isExiting: true })));
    setTimeout(() => {
      setToasts([]);
    }, 200);
  }, []);

  const toast = useMemo(() => ({
    // Basic methods
    show: (message, options = {}) => addToast({ message, type: 'info', ...options }),

    success: (message, options = {}) => addToast({ message, type: 'success', ...options }),

    error: (message, options = {}) => addToast({ message, type: 'error', ...options }),

    warning: (message, options = {}) => addToast({ message, type: 'warning', ...options }),

    info: (message, options = {}) => addToast({ message, type: 'info', ...options }),

    loading: (message, options = {}) => addToast({
      message,
      type: 'loading',
      duration: Infinity,
      dismissible: false,
      ...options,
    }),

    // Utility methods
    dismiss: dismissToast,
    dismissAll,

    // Promise helper
    promise: async (promise, messages) => {
      const id = addToast({
        message: messages.loading || 'Loading...',
        type: 'loading',
        duration: Infinity,
        dismissible: false,
      });

      try {
        const result = await promise;
        addToast({
          id,
          message: typeof messages.success === 'function'
            ? messages.success(result)
            : messages.success || 'Success!',
          type: 'success',
        });
        return result;
      } catch (error) {
        addToast({
          id,
          message: typeof messages.error === 'function'
            ? messages.error(error)
            : messages.error || 'Error!',
          type: 'error',
          description: error?.message,
        });
        throw error;
      }
    },

    // Custom toast
    custom: (content, options = {}) => addToast({
      message: content,
      type: 'info',
      ...options,
    }),
  }), [addToast, dismissToast, dismissAll]);

  const contextValue = useMemo(() => ({
    toast,
    toasts,
  }), [toast, toasts]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer
        toasts={toasts}
        position={position}
        onDismiss={dismissToast}
      />
    </ToastContext.Provider>
  );
};

// ============================================================================
// USE TOAST HOOK
// ============================================================================

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    // Return a mock toast that logs warnings if used outside provider
    console.warn('useToast must be used within a ToastProvider');
    return {
      show: () => {},
      success: () => {},
      error: () => {},
      warning: () => {},
      info: () => {},
      loading: () => {},
      dismiss: () => {},
      dismissAll: () => {},
      promise: async () => {},
      custom: () => {},
    };
  }

  return context.toast;
};

// ============================================================================
// STANDALONE TOAST FUNCTIONS (for use without hooks)
// ============================================================================

let globalToast = null;

export const setGlobalToast = (toastInstance) => {
  globalToast = toastInstance;
};

export const toast = {
  success: (message, options) => globalToast?.success(message, options),
  error: (message, options) => globalToast?.error(message, options),
  warning: (message, options) => globalToast?.warning(message, options),
  info: (message, options) => globalToast?.info(message, options),
  loading: (message, options) => globalToast?.loading(message, options),
  dismiss: (id) => globalToast?.dismiss(id),
  dismissAll: () => globalToast?.dismissAll(),
  promise: (promise, messages) => globalToast?.promise(promise, messages),
};

// ============================================================================
// TOAST PROVIDER WITH GLOBAL REGISTRATION
// ============================================================================

export const ToastProviderWithGlobal = (props) => {
  return (
    <ToastProvider {...props}>
      <ToastGlobalRegistrar />
      {props.children}
    </ToastProvider>
  );
};

const ToastGlobalRegistrar = () => {
  const toastInstance = useToast();

  useEffect(() => {
    setGlobalToast(toastInstance);
    return () => setGlobalToast(null);
  }, [toastInstance]);

  return null;
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default useToast;
